import { z } from "zod";
import { uuid } from "./common";

/*
 * Ayurveda edition A1: the structured case sheet and the Prakriti questionnaire.
 * Choices follow the classical examination schemes (Ashtavidha / Dashavidha pariksha, Agni, Koshta, Srotas) as
 * taught in BAMS case-taking. Everything is optional: a physician records what they examined.
 */

export const DOSHAS = ["vata", "pitta", "kapha"] as const;
export type Dosha = (typeof DOSHAS)[number];
export const DOSHA_LABEL: Record<Dosha, string> = { vata: "Vata", pitta: "Pitta", kapha: "Kapha" };

const opt = <T extends readonly [string, ...string[]]>(values: T) => z.object({ value: z.enum(values).nullish(), note: z.string().trim().max(300).nullish() });

/* --------------------------------------------------------------------------- Ashtavidha pariksha */

export const ASHTAVIDHA = {
  nadi: { label: "Nadi (pulse)", options: ["vata", "pitta", "kapha", "vata_pitta", "pitta_kapha", "vata_kapha", "sama"] },
  mutra: { label: "Mutra (urine)", options: ["normal", "increased", "decreased", "coloured", "burning"] },
  mala: { label: "Mala (stool)", options: ["normal", "hard", "loose", "mucus", "irregular"] },
  jihva: { label: "Jihva (tongue)", options: ["clear", "coated", "dry", "red"] },
  shabda: { label: "Shabda (voice)", options: ["normal", "weak", "hoarse", "loud"] },
  sparsha: { label: "Sparsha (touch)", options: ["normal", "warm", "cold", "dry", "moist"] },
  drik: { label: "Drik (eyes)", options: ["normal", "pale", "yellowish", "reddish", "dry"] },
  akriti: { label: "Akriti (build)", options: ["lean", "medium", "heavy"] },
} as const;
export type AshtavidhaKey = keyof typeof ASHTAVIDHA;

/* --------------------------------------------------------------------------- Dashavidha pariksha */

export const STRENGTH = ["pravara", "madhyama", "avara"] as const; // good / moderate / poor
export const STRENGTH_LABEL: Record<(typeof STRENGTH)[number], string> = { pravara: "Pravara (good)", madhyama: "Madhyama (moderate)", avara: "Avara (poor)" };
export const DASHAVIDHA = {
  sara: "Sara (tissue quality)",
  samhanana: "Samhanana (body compactness)",
  pramana: "Pramana (body proportion)",
  satmya: "Satmya (adaptability)",
  satva: "Satva (mental strength)",
  aharashakti: "Ahara shakti (digestive capacity)",
  vyayamashakti: "Vyayama shakti (exercise capacity)",
} as const;
export type DashavidhaKey = keyof typeof DASHAVIDHA;
export const VAYA = ["bala", "madhya", "vriddha"] as const; // childhood / middle age / old age
export const VAYA_LABEL: Record<(typeof VAYA)[number], string> = { bala: "Bala (up to 16)", madhya: "Madhya (16–60)", vriddha: "Vriddha (over 60)" };

export const AGNI = ["sama", "vishama", "tikshna", "manda"] as const;
export const AGNI_LABEL: Record<(typeof AGNI)[number], string> = { sama: "Sama (balanced)", vishama: "Vishama (irregular)", tikshna: "Tikshna (sharp)", manda: "Manda (weak)" };
export const KOSHTA = ["mridu", "madhyama", "krura"] as const;
export const KOSHTA_LABEL: Record<(typeof KOSHTA)[number], string> = { mridu: "Mridu (soft)", madhyama: "Madhyama (moderate)", krura: "Krura (hard)" };

export const DHATUS = ["rasa", "rakta", "mamsa", "meda", "asthi", "majja", "shukra"] as const;
export const SROTAS = [
  "pranavaha", "udakavaha", "annavaha", "rasavaha", "raktavaha", "mamsavaha", "medovaha", "asthivaha",
  "majjavaha", "shukravaha", "artavavaha", "mutravaha", "purishavaha", "swedavaha", "manovaha",
] as const;

export const NIDANA_PANCHAKA = {
  nidana: "Nidana (causes)",
  purvarupa: "Purvarupa (early signs)",
  rupa: "Rupa (signs and symptoms)",
  upashaya: "Upashaya / Anupashaya (what relieves / worsens)",
  samprapti: "Samprapti (disease process)",
} as const;
export type NidanaKey = keyof typeof NIDANA_PANCHAKA;

const text = (max = 2000) => z.string().trim().max(max).nullish();
const num = (min: number, max: number) => z.number().min(min).max(max).nullish();

export const caseSheetData = z.object({
  vitals: z
    .object({
      bpSystolic: num(50, 260),
      bpDiastolic: num(30, 160),
      pulse: num(30, 220),
      respiration: num(5, 60),
      tempC: num(30, 45),
      spo2: num(50, 100),
      weightKg: num(1, 400),
      heightCm: num(40, 250),
    })
    .partial()
    .default({}),
  pain: z.object({ score: z.number().int().min(0).max(10).nullish(), site: text(200) }).default({}),
  nidana: z.object(Object.fromEntries(Object.keys(NIDANA_PANCHAKA).map((k) => [k, text()])) as Record<NidanaKey, ReturnType<typeof text>>).partial().default({}),
  ashtavidha: z
    .object(
      Object.fromEntries(Object.entries(ASHTAVIDHA).map(([k, v]) => [k, opt(v.options as unknown as [string, ...string[]]).optional()])) as {
        [K in AshtavidhaKey]: z.ZodOptional<ReturnType<typeof opt<(typeof ASHTAVIDHA)[K]["options"]>>>;
      },
    )
    .default({}),
  dashavidha: z
    .object({
      ...(Object.fromEntries(Object.keys(DASHAVIDHA).map((k) => [k, z.enum(STRENGTH).nullish()])) as Record<DashavidhaKey, z.ZodOptional<z.ZodNullable<z.ZodEnum<{ [k in (typeof STRENGTH)[number]]: k }>>>>),
      prakriti: text(100),
      vikriti: z.array(z.enum(DOSHAS)).max(3).nullish(),
      vaya: z.enum(VAYA).nullish(),
    })
    .default({}),
  agni: z.enum(AGNI).nullish(),
  koshta: z.enum(KOSHTA).nullish(),
  involvement: z
    .object({
      doshas: z.array(z.enum(DOSHAS)).max(3).default([]),
      dhatus: z.array(z.enum(DHATUS)).max(7).default([]),
      srotas: z.array(z.enum(SROTAS)).max(15).default([]),
    })
    .default({ doshas: [], dhatus: [], srotas: [] }),
  notes: text(4000),
});
export type CaseSheetData = z.infer<typeof caseSheetData>;
export type CaseSheetInput = z.input<typeof caseSheetData>;

/** BMI from the case sheet's vitals, rounded to one decimal, or null. */
export function bmiOf(v: { weightKg?: number | null; heightCm?: number | null }): number | null {
  if (!v.weightKg || !v.heightCm) return null;
  const m = v.heightCm / 100;
  return Math.round((v.weightKg / (m * m)) * 10) / 10;
}

/* --------------------------------------------------------------------------- Prakriti questionnaire */

/**
 * A screening aid for the patient's constitution (Prakriti) from common classical traits. The physician
 * confirms the final Prakriti; this only adds up the answers.
 */
export const PRAKRITI_QUESTIONS: readonly { key: string; question: string; options: Record<Dosha, string> }[] = [
  { key: "frame", question: "Body frame", options: { vata: "Thin, light, hard to gain weight", pitta: "Medium, muscular", kapha: "Broad, heavy, gains weight easily" } },
  { key: "skin", question: "Skin", options: { vata: "Dry, rough, cracks easily", pitta: "Warm, oily, moles or freckles, flushes", kapha: "Smooth, thick, cool, moist" } },
  { key: "hair", question: "Hair", options: { vata: "Dry, thin, frizzy", pitta: "Fine, early greying or thinning", kapha: "Thick, oily, wavy, lustrous" } },
  { key: "eyes", question: "Eyes", options: { vata: "Small, dry, restless", pitta: "Sharp, penetrating, reddish", kapha: "Large, calm, attractive" } },
  { key: "appetite", question: "Appetite", options: { vata: "Irregular", pitta: "Strong; irritable when hungry", kapha: "Steady; can skip meals easily" } },
  { key: "digestion", question: "Digestion", options: { vata: "Gas, bloating, irregular", pitta: "Quick; acidity or heartburn", kapha: "Slow; heavy after meals" } },
  { key: "bowels", question: "Bowel habit", options: { vata: "Dry, hard, tends to constipation", pitta: "Loose, frequent", kapha: "Regular, well formed, slow" } },
  { key: "thirst", question: "Thirst", options: { vata: "Variable", pitta: "Excessive", kapha: "Low" } },
  { key: "sweat", question: "Sweating", options: { vata: "Little", pitta: "Profuse, strong odour", kapha: "Moderate, steady" } },
  { key: "sleep", question: "Sleep", options: { vata: "Light, interrupted", pitta: "Moderate, sound", kapha: "Deep, long, hard to wake" } },
  { key: "weather", question: "Weather that troubles them", options: { vata: "Cold and wind", pitta: "Heat", kapha: "Cold and damp" } },
  { key: "activity", question: "Way of moving and working", options: { vata: "Quick, restless", pitta: "Purposeful, intense", kapha: "Slow, steady, calm" } },
  { key: "speech", question: "Speech", options: { vata: "Fast, talkative", pitta: "Sharp, precise, argumentative", kapha: "Slow, soft, pleasant" } },
  { key: "mind", question: "Mind", options: { vata: "Quick, creative, changeable", pitta: "Sharp, focused, competitive", kapha: "Calm, steady, methodical" } },
  { key: "memory", question: "Memory", options: { vata: "Learns fast, forgets fast", pitta: "Sharp and clear", kapha: "Slow to learn, remembers long" } },
  { key: "stress", question: "Under stress", options: { vata: "Anxious, worried", pitta: "Irritable, angry", kapha: "Withdrawn, avoids" } },
  { key: "stamina", question: "Stamina", options: { vata: "Low, tires easily", pitta: "Moderate", kapha: "High endurance" } },
  { key: "joints", question: "Joints", options: { vata: "Thin, prominent, cracking", pitta: "Loose, flexible", kapha: "Large, well padded" } },
  { key: "nails", question: "Nails", options: { vata: "Brittle, rough", pitta: "Soft, pink", kapha: "Thick, smooth, strong" } },
  { key: "money", question: "With money", options: { vata: "Spends on impulse", pitta: "Spends with a plan", kapha: "Saves, accumulates" } },
];
export const PRAKRITI_MIN_ANSWERS = 12;

export const prakritiData = z.object({
  answers: z.record(z.string(), z.enum(DOSHAS)).refine((a) => Object.keys(a).every((k) => PRAKRITI_QUESTIONS.some((q) => q.key === k)), "Unknown question"),
  /** The physician's confirmed Prakriti, if it differs from the computed one. */
  confirmed: z.string().trim().max(60).nullish(),
  notes: z.string().trim().max(2000).nullish(),
});
export type PrakritiData = z.infer<typeof prakritiData>;

export interface PrakritiResult {
  answered: number;
  scores: Record<Dosha, number>;
  /** Whole-number percentages that add up to 100. */
  percent: Record<Dosha, number>;
  /** e.g. "Vata", "Pitta-Kapha" or "Sama (balanced)". */
  type: string;
}

/**
 * Adds up the answers. One dosha leads clearly (15+ points ahead) → single type; two lead together (the second
 * 10+ points ahead of the third) → dual type; otherwise balanced ("Sama").
 */
export function prakritiResult(answers: Record<string, Dosha>): PrakritiResult {
  const scores: Record<Dosha, number> = { vata: 0, pitta: 0, kapha: 0 };
  for (const q of PRAKRITI_QUESTIONS) {
    const a = answers[q.key];
    if (a) scores[a] += 1;
  }
  const answered = scores.vata + scores.pitta + scores.kapha;
  const raw = DOSHAS.map((d) => (answered ? (scores[d] / answered) * 100 : 0));
  const floor = raw.map(Math.floor);
  let rest = answered ? 100 - floor.reduce((s, x) => s + x, 0) : 0;
  // Largest remainders get the leftover points so the three add up to exactly 100.
  [...raw.keys()].sort((i, j) => raw[j]! - floor[j]! - (raw[i]! - floor[i]!)).forEach((i) => {
    if (rest > 0) {
      floor[i]! += 1;
      rest -= 1;
    }
  });
  const percent = { vata: floor[0]!, pitta: floor[1]!, kapha: floor[2]! };
  const order = [...DOSHAS].sort((a, b) => percent[b] - percent[a]);
  const [first, second, third] = order.map((d) => percent[d]) as [number, number, number];
  let type: string;
  if (!answered) type = "Not assessed";
  else if (first - second >= 15) type = DOSHA_LABEL[order[0]!];
  else if (second - third >= 10) type = `${DOSHA_LABEL[order[0]!]}-${DOSHA_LABEL[order[1]!]}`;
  else type = "Sama (balanced)";
  return { answered, scores, percent, type };
}

/* --------------------------------------------------------------------------- records and API */

export const ASSESSMENT_KINDS = ["case_sheet", "prakriti"] as const;
export type AssessmentKind = (typeof ASSESSMENT_KINDS)[number];

export const createAssessmentInput = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("case_sheet"), visitId: uuid.nullish(), doctorId: uuid.nullish(), data: caseSheetData }),
  z.object({ kind: z.literal("prakriti"), visitId: uuid.nullish(), doctorId: uuid.nullish(), data: prakritiData }),
]);
export type CreateAssessmentInput = z.input<typeof createAssessmentInput>;

export const assessmentErrorInput = z.object({ reason: z.string().trim().min(3).max(500) });
export const assessmentListQuery = z.object({ kind: z.enum(ASSESSMENT_KINDS).optional(), includeErrors: z.coerce.boolean().default(false) });

export interface AssessmentRecord {
  id: string;
  patientId: string;
  visitId: string | null;
  doctorId: string | null;
  doctorName: string | null;
  kind: AssessmentKind;
  data: CaseSheetData | PrakritiData;
  result: PrakritiResult | { bmi: number | null } | null;
  painScore: number | null;
  weightKg: number | null;
  status: "recorded" | "entered_in_error";
  errorReason: string | null;
  recordedAt: string;
  createdByName: string | null;
}

export interface ReferenceDiagnosis {
  id: number;
  ayurvedaName: string;
  modernName: string;
  icd10: string | null;
}
export const referenceDiagnosisQuery = z.object({ q: z.string().trim().max(60).default("") });

/* --------------------------------------------------------------------------- diet and lifestyle advice (A2) */

const longText = z.string().trim().max(4000).nullish().transform((v) => v || null);

export const dietTemplateInput = z.object({
  name: z.string().trim().min(1).max(120),
  condition: z.string().trim().max(120).nullish().transform((v) => v || null),
  pathyaEn: longText,
  pathyaMl: longText,
  apathyaEn: longText,
  apathyaMl: longText,
  lifestyleEn: longText,
  lifestyleMl: longText,
  isActive: z.boolean().default(true),
});
export type DietTemplateInput = z.input<typeof dietTemplateInput>;

export interface DietTemplate {
  id: string;
  name: string;
  condition: string | null;
  pathyaEn: string | null;
  pathyaMl: string | null;
  apathyaEn: string | null;
  apathyaMl: string | null;
  lifestyleEn: string | null;
  lifestyleMl: string | null;
  isActive: boolean;
  isStarter: boolean;
  updatedAt: string;
}

/** Advice given to a patient: a copy of a template (edited as needed) so later template edits never change it. */
export const dietAdviceInput = dietTemplateInput.omit({ name: true, condition: true, isActive: true }).extend({
  title: z.string().trim().min(1).max(120),
  templateId: uuid.nullish(),
  visitId: uuid.nullish(),
  notes: z.string().trim().max(2000).nullish().transform((v) => v || null),
});
export type DietAdviceInput = z.input<typeof dietAdviceInput>;

export interface DietAdvice {
  id: string;
  patientId: string;
  visitId: string | null;
  templateId: string | null;
  doctorName: string | null;
  title: string;
  pathyaEn: string | null;
  pathyaMl: string | null;
  apathyaEn: string | null;
  apathyaMl: string | null;
  lifestyleEn: string | null;
  lifestyleMl: string | null;
  notes: string | null;
  status: "recorded" | "entered_in_error";
  errorReason: string | null;
  recordedAt: string;
}

/** Print language for patient papers (prescriptions and diet sheets). */
export const PRINT_LANGUAGES = ["en", "ml", "both"] as const;
export type PrintLanguage = (typeof PRINT_LANGUAGES)[number];
